// FILE: src/services/email/emailClickService.js
//
// Click attribution for lifecycle email CTAs.
//
// Every activation reminder links back with campaign params stamped by the
// eligibility sweep: `?view=home&utm_source=lifecycle&utm_medium=email
// &utm_campaign=inactive_24h&ej=<email_job_id>`. On load we hand the job id to
// `record_email_click`, which is the ONLY way a click becomes attributable —
// the alternative (assuming any visit after a send was caused by the send)
// overcounts badly.
//
// Design constraints:
//   • Must work SIGNED OUT. A dormant learner clicking a reminder usually has
//     no session yet, so the RPC is granted to anon and derives the user from
//     the job when auth.uid() is null.
//   • Must never break the page. Every failure path is swallowed; this is
//     analytics, not functionality.
//   • Fire-and-forget. We do not await the result before rendering.

import { supabase } from '../supabaseClient';

/** Query params the email adds that should not linger in the address bar. */
export const EMAIL_CAMPAIGN_PARAMS = ['ej', 'utm_source', 'utm_medium', 'utm_campaign'];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Record a lifecycle-email click if the current URL carries one.
 *
 * @param {string} [search] - Optional query string (defaults to window.location.search).
 * @returns {boolean} true when a click was recognised and reported.
 */
export function captureEmailClick(search) {
  try {
    const qs = typeof search === 'string'
      ? search
      : (typeof window !== 'undefined' ? window.location.search : '');
    if (!qs) return false;

    const params = new URLSearchParams(qs);
    const jobId = (params.get('ej') || '').trim();
    if (!jobId || !UUID_RE.test(jobId)) return false;

    const campaign = (params.get('utm_campaign') || '').trim() || null;

    // Fire-and-forget: a failed insert must never surface to the learner.
    Promise.resolve(
      supabase.rpc('record_email_click', { p_job_id: jobId, p_campaign: campaign })
    ).catch(() => { /* analytics only */ });

    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Strip campaign params from a URL object in place, so a shared or bookmarked
 * link cannot re-report a click. Returns true when anything was removed.
 *
 * @param {URL} url
 * @returns {boolean}
 */
export function stripEmailCampaignParams(url) {
  let changed = false;
  try {
    EMAIL_CAMPAIGN_PARAMS.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });
  } catch (_) { /* not a URL */ }
  return changed;
}
